import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { CenterHeader } from "./center/CenterPrimitives";

/* ---------------------- Big colored KPI tile ---------------------- */

type BigTone = "danger" | "warning" | "success" | "neutral";

function BigKpi({
  label,
  value,
  unit,
  tone = "neutral",
  hint,
}: {
  label: string;
  value: string;
  unit: string;
  tone?: BigTone;
  hint?: string;
}) {
  const bg =
    tone === "danger"
      ? "bg-accent/90 text-accent-foreground"
      : tone === "warning"
        ? "bg-[oklch(0.82_0.16_85)]/90 text-[oklch(0.18_0.04_85)]"
        : tone === "success"
          ? "bg-success/90 text-[oklch(0.16_0.04_150)]"
          : "bg-white/10 text-foreground";
  return (
    <div className="card-surface flex flex-col gap-2 rounded-2xl p-4">
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={`flex flex-col items-center justify-center rounded-xl px-3 py-6 ${bg}`}
      >
        <div className="text-4xl font-light tracking-tight">{value}</div>
        <div className="mt-1 text-xs font-medium uppercase tracking-wider opacity-80">
          {unit}
        </div>
      </div>
      {hint && (
        <div className="text-[11px] text-muted-foreground">{hint}</div>
      )}
    </div>
  );
}

/* ----------------------------- Donut card ----------------------------- */

type Slice = { name: string; value: number; color: string };

function DonutCard({
  title,
  data,
  centerLabel,
  centerValue,
}: {
  title: string;
  data: Slice[];
  centerLabel: string;
  centerValue: string;
}) {
  const total = data.reduce((sum, s) => sum + s.value, 0);
  return (
    <div className="card-surface flex flex-col rounded-2xl p-5">
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      <div className="mt-3 grid grid-cols-[160px_1fr] gap-4">
        <div className="relative h-40 w-40">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                innerRadius={48}
                outerRadius={72}
                paddingAngle={1}
                stroke="transparent"
              >
                {data.map((s, i) => (
                  <Cell key={i} fill={s.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: "oklch(0.18 0.02 240)",
                  border: "1px solid oklch(1 0 0 / 0.1)",
                  borderRadius: 8,
                  fontSize: 11,
                  color: "white",
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-lg font-light">{centerValue}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {centerLabel}
            </div>
          </div>
        </div>
        <ul className="flex flex-col justify-center gap-1.5 text-xs">
          {data.map((s) => {
            const pct = total ? ((s.value / total) * 100).toFixed(1) : "0";
            return (
              <li key={s.name} className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ background: s.color }}
                />
                <span className="min-w-0 flex-1 truncate text-muted-foreground">
                  {s.name}
                </span>
                <span className="font-medium">{pct}%</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/* ----------------------------- Log table ----------------------------- */

type LogRow = { timestamp: string; level: string; component: string; tone: BigTone };

function LogTable({ title, rows }: { title: string; rows: LogRow[] }) {
  const levelBg = (t: BigTone) =>
    t === "danger"
      ? "bg-accent/20 text-accent"
      : t === "warning"
        ? "bg-[oklch(0.82_0.16_85)]/20 text-[oklch(0.85_0.15_85)]"
        : "bg-white/10 text-muted-foreground";
  return (
    <div className="card-surface flex flex-col rounded-2xl p-5">
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      <div className="mt-3 overflow-hidden rounded-xl ring-1 ring-white/5">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-white/[0.03] text-left text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2 font-medium">Timestamp</th>
              <th className="px-3 py-2 font-medium">Level</th>
              <th className="px-3 py-2 font-medium">Component</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-white/5">
                <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                  {r.timestamp}
                </td>
                <td className="px-3 py-2">
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${levelBg(r.tone)}`}>
                    {r.level}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.component}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------------------- View -------------------------------- */

const errorSlices: Slice[] = [
  { name: "o.e.b.Bootstrap", value: 1110000, color: "oklch(0.7 0.2 35)" },
  { name: "o.e.x.s.a.e.ReservedRealm", value: 256000, color: "oklch(0.78 0.16 50)" },
  { name: "o.e.x.t.t.TransformPersistentTasksExecutor", value: 4246, color: "oklch(0.82 0.14 85)" },
];

const fatalSlices: Slice[] = [
  { name: "o.e.n.Node", value: 487, color: "oklch(0.65 0.18 240)" },
  { name: "o.e.c.m.MetadataDeleteIndexService", value: 695, color: "oklch(0.78 0.18 150)" },
  { name: "o.e.p.PluginsService", value: 391, color: "oklch(0.75 0.15 295)" },
  { name: "o.e.x.m.p.NativeController", value: 247, color: "oklch(0.78 0.16 150 / 0.7)" },
  { name: "o.e.c.m.MetadataMappingService", value: 426, color: "oklch(0.72 0.18 35)" },
  { name: "o.e.x.w.WatcherService", value: 149, color: "oklch(0.7 0.14 295 / 0.7)" },
];

const warnSlices: Slice[] = [
  { name: "o.e.x.t.t.TransformTask", value: 1850000, color: "oklch(0.82 0.16 85)" },
  { name: "i.s.s.query", value: 1960000, color: "oklch(0.78 0.16 50)" },
  { name: "o.e.x.m.e.l.LocalExporter", value: 164460, color: "oklch(0.72 0.18 35)" },
];

const errorRows: LogRow[] = [
  { timestamp: "2026-06-22T12:49:51*762+0000", level: "ERROR", component: "o.e.b.Bootstrap", tone: "danger" },
  { timestamp: "2026-06-22T09:18:02*270+0000", level: "ERROR", component: "o.e.b.Bootstrap", tone: "danger" },
  { timestamp: "2026-06-22T08:41:11*012+0000", level: "ERROR", component: "o.e.x.s.a.e.ReservedRealm", tone: "danger" },
];

const fatalRows: LogRow[] = [
  { timestamp: "2026-06-22T01:04:24", level: "FATAL", component: "o.e.c.m.MetadataMappingService", tone: "danger" },
  { timestamp: "2026-06-22T09:10:42", level: "FATAL", component: "o.e.c.m.MetadataDeleteIndexService", tone: "danger" },
  { timestamp: "2026-06-22T12:53:38", level: "FATAL", component: "o.e.c.m.MetadataMappingService", tone: "danger" },
  { timestamp: "2026-06-22T19:52:55", level: "FATAL", component: "o.e.n.Node", tone: "danger" },
  { timestamp: "2026-06-22T12:29:20", level: "FATAL", component: "o.e.c.r.a.AllocationService", tone: "danger" },
];

const warnRows: LogRow[] = [
  { timestamp: "2026-06-22T03:26:23*901+0000", level: "WARN", component: "i.s.s.query", tone: "warning" },
  { timestamp: "2026-06-22T01:03:35*410+0000", level: "WARN", component: "i.s.s.query", tone: "warning" },
  { timestamp: "2026-06-22T18:14:09*220+0000", level: "WARN", component: "o.e.x.m.e.l.LocalExporter", tone: "warning" },
];

export default function SecurityOpsCenterView({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex flex-col gap-5">
      <CenterHeader
        title="Security Operations Center"
        scope="Threats · Audit chain · IoMT · Logs cluster · Incident response"
        onBack={onBack}
      />

      {/* Filter pills */}
      <div className="card-surface flex flex-wrap items-center gap-3 rounded-2xl px-4 py-3 text-xs text-muted-foreground">
        <span className="uppercase tracking-wider">Filtres</span>
        <span className="rounded-md bg-white/5 px-2 py-1">deployment.environment <span className="ml-1 text-foreground">*</span></span>
        <span className="rounded-md bg-white/5 px-2 py-1">db.cluster.name <span className="ml-1 text-foreground">*</span></span>
        <span className="rounded-md bg-white/5 px-2 py-1">db.node.name <span className="ml-1 text-foreground">*</span></span>
        <span className="ml-auto rounded-md bg-white/5 px-2 py-1">⏱ -24h</span>
      </div>

      {/* Big KPI grid */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <BigKpi label="Errors" value="1,372,225" unit="Errors" tone="danger" />
        <BigKpi label="Fatal Errors" value="2,681" unit="Errors" tone="danger" />
        <BigKpi label="Warnings" value="3,969,252" unit="Warnings" tone="warning" />
        <BigKpi label="Shards Failed" value="2,345,371" unit="Shards" tone="danger" />
        <BigKpi label="Rejected Exec." value="2,004,303" unit="Exceptions" tone="danger" hint="-24h" />
        <BigKpi label="Error Mapping Index" value="4,241" unit="Errors" tone="danger" />
      </div>

      {/* Donuts */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <DonutCard
          title="Error Logs By Component"
          data={errorSlices}
          centerLabel="Errors"
          centerValue="1.37M"
        />
        <DonutCard
          title="Fatal Error Logs By Component"
          data={fatalSlices}
          centerLabel="Fatal"
          centerValue="2,681"
        />
        <DonutCard
          title="Warning Logs By Component"
          data={warnSlices}
          centerLabel="Warnings"
          centerValue="3.97M"
        />
      </div>

      {/* Log tables */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <LogTable title="Error Summary — Daily" rows={errorRows} />
        <LogTable title="Fatal Error Summary — Daily" rows={fatalRows} />
        <LogTable title="Warning Summary — Daily" rows={warnRows} />
      </div>

      {/* Exceptions / Log reduce */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[2fr_1fr]">
        <div className="card-surface rounded-2xl p-5">
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Exceptions
          </div>
          <div className="mt-3 overflow-hidden rounded-xl ring-1 ring-white/5">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-white/[0.03] text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Timestamp</th>
                  <th className="px-3 py-2 font-medium">db.node.name</th>
                  <th className="px-3 py-2 font-medium">Level</th>
                  <th className="px-3 py-2 font-medium">Component</th>
                  <th className="px-3 py-2 font-medium">Message</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-white/5">
                  <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">2026-06-22T11:05:57*859+0000</td>
                  <td className="px-3 py-2 font-mono text-[11px]">ip-172-31-86-95.ec2.internal</td>
                  <td className="px-3 py-2">
                    <span className="rounded bg-accent/20 px-1.5 py-0.5 text-[10px] font-semibold text-accent">ERROR</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">o.e.b.Bootstrap</td>
                  <td className="px-3 py-2 text-[11px] text-muted-foreground">
                    node validation [1] bootstrap checks failed: discovery setting must be configured
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-surface rounded-2xl p-5">
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Log Reduce
          </div>
          <div className="mt-3 overflow-hidden rounded-xl ring-1 ring-white/5">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-white/[0.03] text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2 font-medium">db.cluster.name</th>
                  <th className="px-3 py-2 font-medium">Signature</th>
                  <th className="px-3 py-2 text-right font-medium">Count</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-white/5">
                  <td className="px-3 py-2 font-mono text-[11px]">dikdocCluster</td>
                  <td className="px-3 py-2 text-[11px] text-muted-foreground">Others</td>
                  <td className="px-3 py-2 text-right font-mono text-[11px]">3,957,866</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
